/**
 * Command — T-1.3.5.
 *
 * Source of truth: SPEC §4.1 (Command interface) + SPEC §4.3 (undo/redo).
 * Constitution §2.1 — UI does NOT mutate state directly; every state
 * change goes through a Command.
 *
 * The Command interface is intentionally structural: any object that
 * exposes `type`, `apply(state)`, and `revert(state)` qualifies. We do
 * NOT type `apply`/`revert` against the full `SceneState` because:
 *
 *   1. SPEC §2.1 says the data plane is just `voxels: Map` plus a few
 *      other slots. A Command only touches the slots it cares about,
 *      so we accept the narrowest structural type that satisfies what
 *      it needs.
 *   2. This keeps Commands unit-testable on plain `Map`-shaped stubs
 *      (see tests/unit/place-voxel.test.ts) without booting the immer
 *      store. The MainCanvas integration path mutates the live store,
 *      but the *semantics* of apply/revert are pinned by the unit tests.
 *
 * The stack-management helpers (`pushUndo`, `undo`, `redo`) operate
 * against a `CommandHostState` — any state object that has `undoStack`
 * and `redoStack` arrays. Same structural-typing rationale.
 *
 * Undo-stack depth: SPEC §4.3 says "≥50". We cap at exactly 50 — the
 * test pins this contract (see place-voxel.test.ts "caps undoStack at
 * 50 entries"). Bumping the cap is a deliberate decision, not a side
 * effect of `Array.shift` performance characteristics.
 */

import type { VoxelCoord, VoxelProps } from '../state/types';

/**
 * The minimum state shape a Command needs. All real commands touch
 * `voxels` (SPEC §2.1 — single data source); the structural typing
 * here keeps Command unit-testable on plain Map-shaped stubs.
 */
export interface CommandHostState {
  voxels: Map<VoxelCoord, VoxelProps>;
  undoStack: Command[];
  redoStack: Command[];
}

/** The Command contract — every mutating action implements this. */
export interface Command {
  /** Discriminator tag — useful for debugging + M2 conditional undo UI. */
  readonly type: string;
  /** Apply the change to the data plane. */
  apply(state: CommandHostState): void;
  /** Reverse the change made by `apply`. Must be exact inverse. */
  revert(state: CommandHostState): void;
}

/** SPEC §4.3 — undo stack depth cap. */
export const UNDO_STACK_LIMIT = 50;

/**
 * Record a freshly-applied command on the undo stack.
 *
 * Standard branch semantics:
 *   - push onto undoStack
 *   - clear redoStack (any new action invalidates the redo branch)
 *   - trim from the bottom if the cap is exceeded
 *
 * Why trim from the bottom (`shift`) and not the top: the user just
 * performed an action, so the *newest* command is the one they're most
 * likely to want to undo. The oldest command is the natural eviction
 * candidate. SPEC §4.3 only says "≥50", so the direction is our call.
 */
export function pushUndo(state: CommandHostState, command: Command): void {
  state.undoStack.push(command);
  state.redoStack.length = 0;
  while (state.undoStack.length > UNDO_STACK_LIMIT) {
    state.undoStack.shift();
  }
}

/**
 * Pop the top command, revert it, and move it onto the redo stack.
 *
 * Returns `true` if a command was undone, `false` if the undo stack
 * was empty (caller decides whether that's an error or just a no-op).
 */
export function undo(state: CommandHostState): boolean {
  const cmd = state.undoStack.pop();
  if (!cmd) {
    return false;
  }
  cmd.revert(state);
  state.redoStack.push(cmd);
  return true;
}

/**
 * Pop the top redo command, re-apply it, and move it back onto the
 * undo stack.
 *
 * Returns `true` if a command was redone, `false` if the redo stack
 * was empty.
 */
export function redo(state: CommandHostState): boolean {
  const cmd = state.redoStack.pop();
  if (!cmd) {
    return false;
  }
  cmd.apply(state);
  state.undoStack.push(cmd);
  return true;
}
